import { ParseError } from '../../lib/errors.ts';
import { proxiedImageUrl } from '../../lib/proxy.ts';
import type {
  ComicCard,
  ComicDetails,
  FilterFieldDef,
  ListOpts,
  Page,
  PageRef,
  ReadGroup,
  SearchFiltersSchema,
  SourceComment,
  SourceCommentPage,
  SourceId,
  Tag,
} from '../types.ts';

function asRecord(value: unknown, label: string, sourceId: SourceId): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ParseError(`Venera ${label} must be an object`, { source: sourceId });
  }
  return value as Record<string, unknown>;
}

function stringField(record: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);
  }
  return undefined;
}

function numberField(record: Record<string, unknown>, keys: readonly string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

function stringArrayField(record: Record<string, unknown>, keys: readonly string[]): string[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  }
  return [];
}

function stringArrayFromUnknown(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) return [value];
  if (typeof value === 'number') return [String(value)];
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string | number => typeof item === 'string' || typeof item === 'number')
    .map((item) => String(item));
}

function tagFromString(raw: string): Tag {
  const separator = raw.indexOf(':');
  if (separator > 0) {
    const namespace = raw.slice(0, separator);
    const name = raw.slice(separator + 1);
    return { id: name, name, namespace };
  }
  return { id: raw, name: raw };
}

export function mapVeneraTags(value: unknown): Tag[] {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return Object.entries(value as Record<string, unknown>).flatMap(([namespace, rawTags]) => {
      return stringArrayFromUnknown(rawTags)
        .filter((name) => name.trim().length > 0)
        .map((name) => ({ id: `${namespace}:${name}`, name, namespace }));
    });
  }
  if (!Array.isArray(value)) return [];
  return value.flatMap((item): Tag[] => {
    if (typeof item === 'string') return [tagFromString(item)];
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const record = item as Record<string, unknown>;
    const name = stringField(record, ['name', 'title', 'label']);
    if (!name) return [];
    const id = stringField(record, ['id', 'key', 'value']) ?? name;
    const namespace = stringField(record, ['namespace', 'type', 'group']);
    return namespace ? [{ id, name, namespace }] : [{ id, name }];
  });
}

export function mapVeneraComicCard(
  value: unknown,
  sourceId: SourceId,
  label = 'comic',
  fallbackId?: string,
): ComicCard {
  const record = asRecord(value, label, sourceId);
  const id = stringField(record, ['id', 'comicId', '_id', 'path']) ?? fallbackId;
  const title = stringField(record, ['title', 'name']);
  const rawCoverUrl = stringField(record, ['cover', 'coverUrl', 'coverImage']);
  const coverUrl = rawCoverUrl ? normalizeImageUrl(rawCoverUrl, sourceId) : undefined;
  if (!id || !title || !coverUrl) {
    throw new ParseError(`Venera ${label} is missing id, title, or cover`, { source: sourceId });
  }

  const card: ComicCard = {
    id,
    source: sourceId,
    title,
    coverUrl,
  };
  const pageCount = numberField(record, ['pagesCount', 'pageCount', 'pages', 'maxPage']);
  if (pageCount !== undefined) card.pageCount = pageCount;
  const rating = numberField(record, ['rating', 'score']);
  if (rating !== undefined) card.rating = rating;
  const language = stringField(record, ['language', 'lang', 'subTitle']);
  if (language !== undefined) card.language = language;
  const uploadedAt = numberField(record, ['uploadedAt', 'updateTime', 'time']);
  if (uploadedAt !== undefined) card.uploadedAt = uploadedAt;
  const tags = mapVeneraTags(record.tags);
  if (tags.length > 0) card.tags = tags;
  return card;
}

export function mapVeneraComicDetails(
  value: unknown,
  sourceId: SourceId,
  fallbackId?: string,
): ComicDetails {
  const record = asRecord(value, 'comic details', sourceId);
  const card = mapVeneraComicCard(record, sourceId, 'comic details', fallbackId);
  const readGroups = mapVeneraReadGroups(record.chapters);
  const pageCount =
    card.pageCount ??
    numberField(record, ['pagesCount', 'pageCount', 'pages', 'maxPage']) ??
    (readGroups.length > 0 ? 0 : undefined);
  if (pageCount === undefined) {
    throw new ParseError('Venera comic details are missing page count', { source: sourceId });
  }

  const details: ComicDetails = {
    ...card,
    pageCount,
    tags: mapVeneraTags(record.tags),
  };
  if (readGroups.length > 0) details.readGroups = readGroups;
  const altTitle = stringField(record, ['subTitle', 'subtitle', 'altTitle']);
  if (altTitle !== undefined) details.altTitle = altTitle;
  const description = stringField(record, ['description', 'intro', 'summary']);
  if (description !== undefined) details.description = description;
  const uploader =
    stringArrayField(record, ['authors', 'artists'])[0] ??
    stringField(record, ['uploader']) ??
    stringArrayFromUnknown((record.tags as Record<string, unknown> | undefined)?.['作者'])[0] ??
    stringArrayFromUnknown((record.tags as Record<string, unknown> | undefined)?.['Author'])[0];
  if (uploader !== undefined) details.uploader = uploader;
  const sourceUrl = stringField(record, ['url', 'sourceUrl']);
  if (sourceUrl !== undefined) details.sourceUrl = sourceUrl;
  return details;
}

export function mapVeneraReadGroups(chapters: unknown): ReadGroup[] {
  if (chapters && typeof chapters === 'object' && !Array.isArray(chapters)) {
    const entries = Object.entries(chapters as Record<string, unknown>);
    const flatUnits = entries.flatMap(([unitId, rawTitle]) => {
      if (typeof rawTitle !== 'string' && typeof rawTitle !== 'number') return [];
      return [{ id: unitId, title: String(rawTitle) }];
    });
    if (flatUnits.length === entries.length && flatUnits.length > 0) {
      return [{ id: 'default', title: 'Default', units: flatUnits }];
    }
    return entries.flatMap(([groupId, rawGroup]): ReadGroup[] => {
      if (!rawGroup || typeof rawGroup !== 'object' || Array.isArray(rawGroup)) return [];
      const units = Object.entries(rawGroup as Record<string, unknown>).map(([unitId, rawTitle]) => ({
        id: unitId,
        title: typeof rawTitle === 'string' && rawTitle.trim() ? rawTitle : unitId,
      }));
      return units.length > 0 ? [{ id: groupId, title: groupId, units }] : [];
    });
  }
  if (!Array.isArray(chapters)) return [];
  return chapters.flatMap((rawGroup, groupIndex): ReadGroup[] => {
    if (!rawGroup || typeof rawGroup !== 'object' || Array.isArray(rawGroup)) return [];
    const groupRecord = rawGroup as Record<string, unknown>;
    const groupTitle = stringField(groupRecord, ['title', 'name', 'label']) ?? `Group ${groupIndex + 1}`;
    const groupId = stringField(groupRecord, ['id', 'key', 'path']) ?? groupTitle;
    const data = groupRecord.data;
    const units = Array.isArray(data)
      ? data.flatMap((rawUnit, unitIndex): ReadGroup['units'] => {
          if (Array.isArray(rawUnit)) return [];
          if (rawUnit && typeof rawUnit === 'object') {
            const unitRecord = rawUnit as Record<string, unknown>;
            const id = stringField(unitRecord, ['id', 'chapterId', 'uuid', 'path']) ?? String(unitIndex + 1);
            const title = stringField(unitRecord, ['title', 'name', 'chapter_title']) ?? id;
            return [{ id, title }];
          }
          if (typeof rawUnit === 'string' || typeof rawUnit === 'number') {
            return [{ id: String(rawUnit), title: String(rawUnit) }];
          }
          return [];
        })
      : [];
    return units.length > 0 ? [{ id: groupId, title: groupTitle, units }] : [];
  });
}

export function firstChapterId(details: unknown, sourceId: SourceId): string | undefined {
  const record = asRecord(details, 'comic details', sourceId);
  const chapters = record.chapters;
  if (chapters && typeof chapters === 'object' && !Array.isArray(chapters)) {
    const firstEntry = Object.entries(chapters as Record<string, unknown>)[0];
    const firstGroup = firstEntry?.[1];
    if (firstGroup && typeof firstGroup === 'object' && !Array.isArray(firstGroup)) {
      return Object.keys(firstGroup)[0];
    }
    return firstEntry?.[0];
  }
  if (!Array.isArray(chapters)) return undefined;
  const first = chapters[0];
  if (!first || typeof first !== 'object' || Array.isArray(first)) return undefined;
  return stringField(first as Record<string, unknown>, ['id', 'chapterId', 'path']);
}

export function mapVeneraPage(value: unknown, sourceId: SourceId, comicId: string, index: number): PageRef {
  if (typeof value === 'string' && value.trim()) {
    return { comicId, index, resolvedUrl: normalizeImageUrl(value, sourceId) };
  }
  const record = asRecord(value, 'page', sourceId);
  const rawUrl = stringField(record, ['url', 'src', 'imageUrl']);
  const url = rawUrl ? normalizeImageUrl(rawUrl, sourceId) : undefined;
  if (!url) {
    throw new ParseError('Venera page is missing image URL', { source: sourceId });
  }
  const rawThumbnailUrl = stringField(record, ['thumbnail', 'thumbnailUrl']);
  const thumbnailUrl = rawThumbnailUrl ? normalizeImageUrl(rawThumbnailUrl, sourceId) : undefined;
  const page: PageRef = { comicId, index, resolvedUrl: url };
  if (thumbnailUrl !== undefined) page.thumbnailUrl = thumbnailUrl;
  return page;
}

function normalizeImageUrl(url: string, sourceId: SourceId): string {
  if (sourceId !== 'zaimanhua') return url;
  try {
    const host = new URL(url).host;
    if (host === 'images.zaimanhua.com' || host === 'i.zaimanhua.com') {
      return proxiedImageUrl(url);
    }
  } catch {
    return url;
  }
  return url;
}

export function mapVeneraPageResult(
  value: unknown,
  sourceId: SourceId,
  opts: { page?: number } = {},
): Page<ComicCard> {
  const record = asRecord(value, 'page result', sourceId);
  const comics = normalizePageComics(record);
  if (!Array.isArray(comics)) {
    throw new ParseError('Venera page result is missing comics array', { source: sourceId });
  }
  const page = numberField(record, ['page']) ?? opts.page ?? 1;
  const totalPages = numberField(record, ['maxPage', 'totalPages']);
  const totalItems = numberField(record, ['totalItems', 'total']);
  return {
    items: comics.map((comic, index) => mapVeneraComicCard(comic, sourceId, `comic ${index}`)),
    page,
    hasMore: totalPages !== undefined ? page < totalPages : comics.length > 0,
    totalPages,
    totalItems,
  };
}

function normalizePageComics(record: Record<string, unknown>): unknown {
  if (Array.isArray(record.comics)) return record.comics;
  if (Array.isArray(record.data)) {
    return record.data.flatMap((entry) => {
      if (Array.isArray(entry)) return entry;
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
      const nested = (entry as Record<string, unknown>).comics;
      return Array.isArray(nested) ? nested : [];
    });
  }
  const grouped = Object.values(record).flatMap((entry) => (Array.isArray(entry) ? entry : []));
  return grouped.length > 0 ? grouped : undefined;
}

export function mapVeneraCommentPage(
  value: unknown,
  sourceId: SourceId,
  opts: { page?: number; parentId?: string } = {},
): SourceCommentPage {
  const record = asRecord(value, 'comment page', sourceId);
  const rawComments = Array.isArray(record.comments) ? record.comments : Array.isArray(record.data) ? record.data : undefined;
  if (!Array.isArray(rawComments)) {
    throw new ParseError('Venera comment page is missing comments array', { source: sourceId });
  }
  const page = numberField(record, ['page']) ?? opts.page ?? 1;
  const totalPages = numberField(record, ['maxPage', 'totalPages']);
  const totalItems = numberField(record, ['totalItems', 'total']);
  const commentPage: SourceCommentPage = {
    items: rawComments.map((comment, index) => mapVeneraComment(comment, sourceId, index, opts.parentId)),
    page,
    hasMore: totalPages !== undefined ? page < totalPages : rawComments.length > 0,
  };
  if (totalPages !== undefined) commentPage.totalPages = totalPages;
  if (totalItems !== undefined) commentPage.totalItems = totalItems;
  return commentPage;
}

function mapVeneraComment(
  value: unknown,
  sourceId: SourceId,
  index: number,
  fallbackParentId?: string,
): SourceComment {
  const record = asRecord(value, `comment ${index}`, sourceId);
  const id = stringField(record, ['id', 'commentId', '_id']) ?? String(index);
  const authorName = stringField(record, ['userName', 'authorName', 'author', 'username', 'name']);
  const body = stringField(record, ['content', 'body', 'comment', 'text']);
  if (!authorName || !body) {
    throw new ParseError('Venera comment is missing author or body', { source: sourceId });
  }

  const comment: SourceComment = {
    id,
    authorName,
    body,
  };
  const authorAvatarUrl = stringField(record, ['avatar', 'authorAvatarUrl', 'avatarUrl']);
  if (authorAvatarUrl !== undefined) comment.authorAvatarUrl = authorAvatarUrl;
  const createdAt = timestampField(record, ['time', 'createdAt', 'created_at', 'createAt']);
  if (createdAt !== undefined) comment.createdAt = createdAt;
  const score = numberField(record, ['score', 'likesCount', 'likeCount']);
  if (score !== undefined) comment.score = score;
  const replyCount = numberField(record, ['replyCount', 'commentsCount']);
  if (replyCount !== undefined) comment.replyCount = replyCount;
  const parentId = stringField(record, ['parentId', 'replyTo']) ?? fallbackParentId;
  if (parentId !== undefined) comment.parentId = parentId;
  return comment;
}

function timestampField(record: Record<string, unknown>, keys: readonly string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
      const parsed = Date.parse(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

export function mapOptionListToSearchFilters(optionList: unknown): SearchFiltersSchema | undefined {
  if (!Array.isArray(optionList)) return undefined;
  const fields = optionList.flatMap((item): FilterFieldDef[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const record = item as Record<string, unknown>;
    const id = stringField(record, ['key', 'id']) ?? defaultOptionId(record);
    const label = stringField(record, ['label', 'name']) ?? id;
    if (!id || !label) return [];
    const type = stringField(record, ['type']);
    if ((type === 'select' || type === undefined) && Array.isArray(record.options)) {
      const options = record.options.flatMap((option): { value: string; label: string }[] => {
        if (typeof option === 'string') {
          const separator = option.indexOf('-');
          if (separator > 0) {
            return [{ value: option.slice(0, separator), label: option.slice(separator + 1) }];
          }
          return [{ value: option, label: option }];
        }
        if (!option || typeof option !== 'object' || Array.isArray(option)) return [];
        const optionRecord = option as Record<string, unknown>;
        const value = stringField(optionRecord, ['value', 'key', 'id']);
        const optionLabel = stringField(optionRecord, ['label', 'name']) ?? value;
        return value && optionLabel ? [{ value, label: optionLabel }] : [];
      });
      return options.length > 0 ? [{ id, type: 'select', label, options }] : [];
    }
    if (type === 'toggle') return [{ id, type: 'toggle', label }];
    if (type === 'number') return [{ id, type: 'number', label }];
    return [];
  });
  return fields.length > 0 ? { fields } : undefined;
}

export function pageFromListOpts(opts: ListOpts): number {
  return Math.max(1, opts.page ?? 1);
}

function defaultOptionId(record: Record<string, unknown>): string | undefined {
  const label = stringField(record, ['label', 'name']);
  if (label?.toLowerCase() === 'sort') return 'sort';
  return undefined;
}
